import { useMemo, type ReactNode } from "react";
import { Grid, Button, MenuItem } from "@mui/material";
import type { PersonneCreateDto } from "models";
import { elusService } from "services";
import { ErrorNames, useSnackbarErrorHandler } from "utils/errorHandling";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { routesConfig } from "config/app-config";
import { useNavigate } from "react-router";
import { useSearch } from "providers";
import ControlledTextField from "components/inputs/ControlledTextField";
import ControlledDateTime from "components/inputs/ControlledDateTime";
import ToastMessages from "constants/ToastMessages";
import { enqueueSnackbar } from "notistack";
import { validDateRules } from "constants/InputRules";
import { dateUtil } from "@sdeapps/react-core";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import { NameHelperSuggestion } from "./NameHelperSuggestion";

function replacer(_whole: string, prefix: string, word: string): string {
  const ret: Array<string> = [];

  if (prefix != null && prefix !== "" && prefix.length > 1) {
    ret.push(prefix.charAt(0).toLocaleUpperCase("fr-FR"));
    ret.push(prefix.substring(1).toLocaleLowerCase("fr-FR"));
  }

  ret.push(word.charAt(0).toLocaleUpperCase("fr-FR"));
  ret.push(word.substring(1).toLocaleLowerCase("fr-FR"));
  return ret.join("");
}

// Capitalize les prénoms en prenant en compte les cas particuliers comme apostrophes ou tirets (ex: "Jean-Michel", "O'Neil")
// credit: https://bearandhammer.net/2016/04/18/groovy-javascript-regex-name-capitalisation-handling/
function fixNameCasing(name: string): string {
  const pattern = /\b(ma?c)?([a-z]+)/gi;
  if (name.length > 1) {
    return name.replaceAll(pattern, replacer);
  }
  return name;
}

/** 1er Janvier 1930 */
const MIN_DATE_1930: Date = new Date(1930, 0, 1);
/** Il y a 18 ans */
const MAX_DATE_18_YEARS_AGO: Date = dateUtil.addDuration(new Date(), { years: -18 });

function PersonneForm(): ReactNode {
  const navigate = useNavigate();
  const { update } = useSearch();

  const formMethods = useForm<PersonneCreateDto>();
  const { handleSubmit, control } = formMethods;
  const prenom = useWatch({ control, name: "prenom" });
  const nom = useWatch({ control, name: "nom" });

  const prenomSuggestion = useMemo(() => {
    if (prenom == null || prenom === "") {
      return "";
    }
    return fixNameCasing(prenom);
  }, [prenom]);
  const nomSuggestion = useMemo(() => {
    if (nom == null || nom === "") {
      return "";
    }
    return nom.toLocaleUpperCase("fr-FR");
  }, [nom]);

  const { catchErrors: catchCreateErrors, isLoading: isSending } = useSnackbarErrorHandler({
    [ErrorNames.Conflict]: () => {
      enqueueSnackbar({
        variant: "error",
        message: ToastMessages.ERROR_DOUBLON_PERSONNE,
      });
    },
  });

  async function submitPersonne(data: PersonneCreateDto): Promise<void> {
    async function createElu(): Promise<void> {
      await elusService.create(data);

      enqueueSnackbar({
        variant: "success",
        message: ToastMessages.CREATED_PERSONNE,
      });

      update();
      navigate(routesConfig.admin.path);
    }
    await catchCreateErrors(createElu);
  }

  return (
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    <Grid component="form" onSubmit={handleSubmit(submitPersonne)} container spacing={4} size={12}>
      <FormProvider {...formMethods}>
        <Grid container spacing={2} size={12}>
          <Grid size={{ xs: 12, md: 2 }}>
            <ControlledTextField
              select
              name="sexe"
              rules={{ required: "Saisir une civilité est obligatoire" }}
              label="Civilité *">
              <MenuItem value="M">M</MenuItem>
              <MenuItem value="F">F</MenuItem>
            </ControlledTextField>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <ControlledTextField
              name="nom"
              defaultValue=""
              rules={{ required: "Saisir un nom est obligatoire" }}
              fullWidth
              multiline={false}
              label="Nom *"
              variant="outlined"
              placeholder="DUPONT"
              defaultHelperText={
                <NameHelperSuggestion
                  original={nom}
                  suggestion={nomSuggestion}
                  setValue={(value: string) => {
                    formMethods.setValue("nom", value);
                  }}
                  defaultHelpertext="En majuscules"
                />
              }
            />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <ControlledTextField
              name="prenom"
              defaultValue=""
              rules={{ required: "Saisir un prénom est obligatoire" }}
              fullWidth
              multiline={false}
              label="Prénom *"
              variant="outlined"
              placeholder="Jean-Michel"
              defaultHelperText={
                <NameHelperSuggestion
                  original={prenom}
                  suggestion={prenomSuggestion}
                  setValue={(value: string) => {
                    formMethods.setValue("prenom", value);
                  }}
                  defaultHelpertext="Avec une Majuscule au début et le reste en minuscules"
                />
              }
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <ControlledDateTime
              name="dateNaissance"
              minDate={MIN_DATE_1930}
              maxDate={MAX_DATE_18_YEARS_AGO}
              rules={{
                required: "Choisir une date de naissance est obligatoire",
                ...validDateRules,
                validate: (value: Date | null) =>
                  (dateUtil.isValid(value) &&
                    dateUtil.isAfter(value, MIN_DATE_1930) &&
                    dateUtil.isBefore(value, MAX_DATE_18_YEARS_AGO)) ||
                  "Veuillez renseigner une date entre 1930 et il y a 18 ans.",
              }}
              label="Date de naissance *"
            />
          </Grid>
        </Grid>
        <Grid container size={12} sx={{ justifyContent: "flex-end" }} spacing={2}>
          <Button href={routesConfig.admin.path} color="error">
            Annuler
          </Button>
          <Button
            variant="contained"
            loadingPosition="start"
            type="submit"
            loading={isSending}
            startIcon={<PersonAddAlt1Icon />}>
            Créer une nouvelle personne
          </Button>
        </Grid>
      </FormProvider>
    </Grid>
  );
}

export default PersonneForm;
